import React, { FC, useCallback, useState } from "react";
import { Button } from "@xapp/components";

import { BasicIntegrationCard, IntegrationCardProp } from "./BasicIntegrationCard";
import { LeadConnectDialogContainer } from "../containers/LeadConnectDialogContainer";
import { LEAD_CONNECT_NAME } from "../integrationCatalog";

export interface LeadConnectIntegrationCardProps extends IntegrationCardProp {
    organizationId: string;
    /** The integration or add-on changed: refetch the integrations page. */
    onChanged?(): void;
}

/**
 * Lead Connect, a paid add-on. Clicking the card buys it or sets it up; once set up, "Lead
 * Connect Status" shows the number, places a test call and cancels the add-on.
 *
 * The dialog's container is mounted only while it is open, so its queries run only then without
 * pausing a query on the dialog's open state.
 */
export const LeadConnectIntegrationCard: FC<LeadConnectIntegrationCardProps> = (props) => {
    const { appId, organizationId, checked, disabled, isLinked, isActive, onDisconnect, onChanged } = props;

    const [showDialog, setShowDialog] = useState<boolean>(false);

    const handleClick = useCallback(
        (e: React.MouseEvent<HTMLAnchorElement>): void => {
            e.preventDefault();

            if (typeof checked === "boolean") {
                if (typeof props.onClick === "function") {
                    props.onClick(e);
                }
            } else if (!disabled) {
                setShowDialog(true);
            }
        },
        [checked, disabled, props.onClick]
    );

    return (
        <>
            <BasicIntegrationCard
                checked={checked}
                disabled={disabled}
                imageUrl="/img/integrations/lead-connect/lead-connect-logo.svg"
                integrationId="leadconnect"
                text={LEAD_CONNECT_NAME}
                href={() => "/integrations/leadconnect"}
                onClick={handleClick}
                isLinked={isLinked}
                isActive={isActive}
                onDisconnect={onDisconnect}
            />
            {isLinked && (
                <div style={{ margin: "8px 0 16px" }}>
                    <Button data-lead-connect-status-button variation="secondary" onClick={() => setShowDialog(true)}>
                        {LEAD_CONNECT_NAME} Status
                    </Button>
                </div>
            )}
            {showDialog && (
                <LeadConnectDialogContainer
                    appId={appId}
                    organizationId={organizationId}
                    active={showDialog}
                    onClose={() => setShowDialog(false)}
                    onChanged={onChanged}
                />
            )}
        </>
    );
};
